// ------------------------------
// components/SendOnchain.tsx
// Send on-chain payment screen
// ------------------------------

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Text,
  Center,
  Stack,
  Title,
  Group,
  Button,
  TextInput,
  NumberInput,
  Alert,
  Box,
  Checkbox,
  Paper,
  ThemeIcon,
  Divider,
  Anchor,
} from "@mantine/core";
import {
  IconCoinBitcoinFilled,
  IconSend2,
  IconReceipt,
  IconAlertTriangle,
  IconRefresh,
  IconCheck,
  IconExternalLink,
} from "@tabler/icons-react";
import { useState } from "react";

import { BobsWorkspace } from "../stores/BobsWorkspace";
import { DestinationAddress } from "./details/DestinationAddress";
import { formatFiat } from "../helpers/formatNumbers";

// Convert satoshis to BTC string
const satoshisToBtc = (satoshis: number | null): string => {
  if (satoshis === null || satoshis === undefined) return "0";
  return (satoshis / 100_000_000).toFixed(8);
};

const SendOnchain = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const { selectedBob } = bobsWorkspace;
    const [addressConfirmed, setAddressConfirmed] = useState(false);

    // Reset function for starting over or clearing address
    const resetProposal = () => {
      selectedBob?.deleteAllOnchainPaymentProposals();
      selectedBob?.createAndSelectNewOnchainPaymentProposal();
      setAddressConfirmed(false);
    };

    useEffect(() => {
      if (selectedBob) {
        selectedBob.createAndSelectNewOnchainPaymentProposal();
      }
      return () => {
        if (selectedBob) {
          selectedBob.deleteAllOnchainPaymentProposals();
        }
      };
    }, [selectedBob]);

    const proposal = selectedBob?.selectedOnchainPaymentProposal;

    return (
      <Stack>
        <Center>
          <Group gap="xs">
            <IconCoinBitcoinFilled size={28} />
            <Title order={3}>Send On-Chain</Title>
          </Group>
        </Center>

        {!proposal?.uuidFromServer && (
          <Center>
            <Text size="sm" c="dimmed">
              Sending Bitcoin on-chain can be slow. Use Lightning for faster
              payments.
            </Text>
          </Center>
        )}

        {proposal && (
          <Stack gap="md" mt="md">
            {/* Address Entry or Display - hide when quote received */}
            {!proposal.uuidFromServer && (
              <>
                {!proposal.validProposedAddress ? (
                  <Box>
                    <TextInput
                      label="Destination Bitcoin Address"
                      placeholder="Enter a Bitcoin address"
                      value={proposal.proposedAddress || ""}
                      onChange={(e) =>
                        proposal.setProposedAddress(e.currentTarget.value)
                      }
                      error={proposal.userFacingAddressErrorMessage}
                    />
                  </Box>
                ) : (
                  <DestinationAddress
                    address={proposal.validProposedAddress}
                    label="Destination Address"
                    onClear={resetProposal}
                  />
                )}
              </>
            )}

            {/* Amount Entry - only show when address is valid and no quote yet */}
            {proposal.validProposedAddress && !proposal.uuidFromServer && (
              <Box>
                <NumberInput
                  label="Amount (satoshis)"
                  placeholder="Enter amount in satoshis"
                  value={proposal.proposedAmountSatoshis || ""}
                  onChange={(value) => {
                    // Mantine passes a string (not a number) for empty input
                    // and for values with 14+ digits, so parse strings too
                    if (typeof value === "number") {
                      proposal.setProposedAmountSatoshis(value);
                    } else if (value.trim() === "") {
                      proposal.setProposedAmountSatoshis(null);
                    } else {
                      proposal.setProposedAmountSatoshis(
                        Number(value.replace(/,/g, "")),
                      );
                    }
                  }}
                  min={0}
                  allowNegative={false}
                  allowDecimal={false}
                  thousandSeparator=","
                  error={proposal.userFacingProposedAmountErrorMessage}
                />
                {proposal.proposedAmountSatoshis !== null &&
                  proposal.proposedAmountSatoshis > 0 && (
                    <Text size="sm" c="dimmed" mt="xs">
                      {satoshisToBtc(proposal.proposedAmountSatoshis)} BTC
                      {selectedBob && (
                        <Text span c="dimmed" ml="xs">
                          (
                          {formatFiat({
                            satoshis: proposal.proposedAmountSatoshis,
                            currency: selectedBob.currencyCode,
                            currencySnapshot: bobsWorkspace.exchangeRates.data,
                          })}
                          )
                        </Text>
                      )}
                    </Text>
                  )}
              </Box>
            )}

            {/* Initiate Payment Button - only show when no quote yet */}
            {proposal.proposedAmountSatoshisIsValid &&
              !proposal.uuidFromServer && (
                <Button
                  loading={proposal.bob.areWeWaitingOnRails}
                  disabled={proposal.bob.areWeWaitingOnRails}
                  leftSection={<IconSend2 size={18} />}
                  size="md"
                  mt="md"
                  onClick={() =>
                    proposal.makeNewOnchainOutboundTransactionQuote()
                  }
                >
                  Initiate Payment
                </Button>
              )}

            {/* Quote Received - Show fee breakdown and confirmation */}
            {proposal.uuidFromServer && (
              <Stack gap="md" mt="md">
                <Paper p="md" radius="md" withBorder>
                  <Stack gap="sm">
                    <Group gap="xs">
                      <ThemeIcon variant="light" color="blue" size="md">
                        <IconReceipt size={16} />
                      </ThemeIcon>
                      <Text fw={600}>Payment Summary</Text>
                    </Group>

                    <Divider />

                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Amount to send
                      </Text>
                      <Stack gap={0} align="flex-end">
                        <Text size="sm" fw={500}>
                          {proposal.proposedAmountSatoshis?.toLocaleString()}{" "}
                          sats
                        </Text>
                        {selectedBob && proposal.proposedAmountSatoshis && (
                          <Text size="xs" c="dimmed">
                            {formatFiat({
                              satoshis: proposal.proposedAmountSatoshis,
                              currency: selectedBob.currencyCode,
                              currencySnapshot:
                                bobsWorkspace.exchangeRates.data,
                            })}
                          </Text>
                        )}
                      </Stack>
                    </Group>

                    <Group justify="space-between">
                      <Stack gap={0}>
                        <Text size="sm" c="dimmed">
                          Platform fee (
                          {(
                            (bobsWorkspace.weKeepThisPlatformFeePercentageForOnchainPayments ||
                              0.004) * 100
                          ).toFixed(1)}
                          %)
                        </Text>
                        <Text size="xs" c="dimmed">
                          + onchain fees
                        </Text>
                      </Stack>
                      <Stack gap={0} align="flex-end">
                        <Text size="sm" fw={500}>
                          {proposal.platformFeeSatoshisFromServer?.toLocaleString()}{" "}
                          sats
                        </Text>
                        {selectedBob &&
                          proposal.platformFeeSatoshisFromServer && (
                            <Text size="xs" c="dimmed">
                              {formatFiat({
                                satoshis:
                                  proposal.platformFeeSatoshisFromServer,
                                currency: selectedBob.currencyCode,
                                currencySnapshot:
                                  bobsWorkspace.exchangeRates.data,
                              })}
                            </Text>
                          )}
                      </Stack>
                    </Group>

                    <Divider />

                    <Group justify="space-between">
                      <Text size="sm" fw={600}>
                        Total
                      </Text>
                      <Stack gap={0} align="flex-end">
                        <Text size="sm" fw={600}>
                          {(
                            (proposal.proposedAmountSatoshis || 0) +
                            (proposal.platformFeeSatoshisFromServer || 0)
                          ).toLocaleString()}{" "}
                          sats
                        </Text>
                        {selectedBob && (
                          <Text size="xs" c="dimmed">
                            {formatFiat({
                              satoshis:
                                (proposal.proposedAmountSatoshis || 0) +
                                (proposal.platformFeeSatoshisFromServer || 0),
                              currency: selectedBob.currencyCode,
                              currencySnapshot:
                                bobsWorkspace.exchangeRates.data,
                            })}
                          </Text>
                        )}
                      </Stack>
                    </Group>
                  </Stack>
                </Paper>

                <DestinationAddress
                  address={proposal.validProposedAddress!}
                  label="Destination Address"
                  onClear={resetProposal}
                />

                <Alert
                  icon={<IconAlertTriangle size={18} />}
                  color="orange"
                  variant="light"
                >
                  <Checkbox
                    checked={addressConfirmed}
                    onChange={(e) =>
                      setAddressConfirmed(e.currentTarget.checked)
                    }
                    label="I confirm the above Bitcoin address is correct, and I acknowledge that on-chain payments are irreversible."
                    styles={{ body: { cursor: "default" } }}
                  />
                </Alert>

                {/* 2FA Code Input - only show if required */}
                {bobsWorkspace.accountRequiresTwoFactorFormForActions && (
                  <TextInput
                    label="Two-Factor Authentication Code"
                    placeholder="Enter your 2FA code"
                    value={proposal.userProvided2FaCode || ""}
                    onChange={(e) =>
                      proposal.setUserProvided2FaCode(e.currentTarget.value)
                    }
                    maxLength={6}
                  />
                )}

                {/* Show transaction confirmed notice if we have a transaction ID */}
                {proposal.confirmedTransactionIdFromServer ? (
                  <Alert
                    icon={<IconCheck size={18} />}
                    color="green"
                    variant="light"
                    title="Transaction Broadcast"
                  >
                    <Stack gap="xs">
                      <Text size="sm">
                        Your on-chain payment has been broadcast to the network.
                      </Text>
                      <Anchor
                        href={`https://mempool.space/tx/${proposal.confirmedTransactionIdFromServer}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                      >
                        <Group gap={4}>
                          View on mempool.space
                          <IconExternalLink size={14} />
                        </Group>
                      </Anchor>
                    </Stack>
                  </Alert>
                ) : (
                  <Button
                    leftSection={<IconSend2 size={18} />}
                    size="md"
                    onClick={() => proposal.sendOnchainPayment()}
                    color="green"
                    loading={proposal.bob.areWeWaitingOnRails}
                    disabled={
                      !addressConfirmed ||
                      proposal.bob.areWeWaitingOnRails ||
                      (bobsWorkspace.accountRequiresTwoFactorFormForActions &&
                        (proposal.userProvided2FaCode?.length || 0) < 6)
                    }
                  >
                    Confirm & Send Payment
                  </Button>
                )}
              </Stack>
            )}

            {/* Start Over Button - show when there's an error or payment summary is visible */}
            {(proposal.userFacingAddressErrorMessage ||
              proposal.userFacingProposedAmountErrorMessage ||
              proposal.uuidFromServer) && (
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={resetProposal}
                >
                  Start Over
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);

export default SendOnchain;
